import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Countdown from '../components/Countdown';
import StemningSection from '../components/StemningSection';
import WhiteLogo from '../components/WhiteLogo';
import OrBike365Section from '../components/OrBike365Section';
import { getActiveEvent, getProgram, getPartnere, getVideoer, getGallerier, getVelgoerendeFormaal, FALLBACK_PARTNERE } from '../lib/cms';

export default function Forside() {
  const [eventData, setEventData] = useState(null);
  const [program, setProgram] = useState([]);
  const [partnere, setPartnere] = useState(FALLBACK_PARTNERE);
  const [videoer, setVideoer] = useState([]);
  const [gallerier, setGallerier] = useState([]);
  const [formaal, setFormaal] = useState([]);

  useEffect(() => {
    getActiveEvent().then(setEventData);
    getProgram().then(setProgram);
    getPartnere().then((data) => {
      if (data && data.length > 0) {
        setPartnere(data);
      }
    });
    getVideoer().then(setVideoer);
    getGallerier().then(setGallerier);
    getVelgoerendeFormaal().then(setFormaal);
  }, []);

  const billetlink = eventData?.billetlink || "https://billet.eventbilletten.dk/seller/foreningen-rbike-zd1k";
  const heroImg = eventData?.hero_billede?.[0] || "https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/dreamfour-9995.jpg";

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen">
      <Helmet>
        <title>Foreningen ØrBike</title>
        <meta 
          name="description" 
          content="ØrBike er med +230 cykler et af Danmarks største indoor bike events i Ørbæk Midtpunkt. 10 timers fest, musik og spinning til fordel for børn med kritisk sygdom." 
        />
        <meta property="og:title" content="Foreningen ØrBike – Danmarks største bike fest" />
        <meta property="og:description" content="10 timers spinning for en god sag den 14. november 2026 i Ørbæk Midtpunkt." />
        <meta property="og:image" content={heroImg} />
      </Helmet>

      {/* HERO SECTION – KONCERT- OG FESTIVALPLAKAT-STIL */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-32 sm:pt-40 lg:pt-48 pb-16 sm:pb-20 lg:pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Baggrundsvideo med overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            poster={heroImg}
            className="w-full h-full object-cover object-center brightness-[0.35] contrast-125"
          >
            <source src="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/oerbike-2025.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[#090a0f] via-[#090a0f]/60 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-pink-900/20 via-transparent to-purple-900/20 mix-blend-color-dodge"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center flex flex-col items-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-300 text-xs sm:text-sm font-bold uppercase tracking-widest shadow-[0_0_20px_rgba(255,42,141,0.3)] mb-6 animate-pulse">
            <i className="fa-solid fa-bolt text-pink-400" aria-hidden="true"></i>
            <span>10 timers indoor bike fest • over 230 cykler</span>
          </div>

          {/* Hovedtitel i gigantisk display-stil */}
          <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black font-display tracking-tight text-white uppercase leading-none drop-shadow-[0_4px_25px_rgba(0,0,0,0.8)]">
            ØRBIKE <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">2026</span>
          </h1>

          {/* Dato og Lokation */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-base sm:text-2xl font-bold font-display uppercase tracking-wider text-slate-200">
            <span className="flex items-center gap-2 text-pink-400">
              <i className="fa-solid fa-calendar-days" aria-hidden="true"></i>
              Lørdag 14. november 2026
            </span>
            <span className="hidden sm:inline text-pink-500">•</span>
            <span className="flex items-center gap-2 text-purple-300">
              <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
              Ørbæk Midtpunkt
            </span>
          </div>

          {/* Velgørende formål underoverskrift */}
          <p className="mt-5 max-w-2xl text-slate-300 text-sm sm:text-base md:text-lg leading-relaxed font-medium">
            Sammen skaber vi smil, ét tråd ad gangen. Vi cykler for livet og donerer overskuddet til børn med kritisk sygdom.
          </p>

          {/* CTA Knapper */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
            <a
              href={billetlink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 rounded-full bg-gradient-to-r from-pink-500 via-purple-600 to-pink-500 bg-[length:200%_auto] hover:bg-[position:right_center] text-white font-extrabold text-base uppercase tracking-wider shadow-[0_0_30px_rgba(255,42,141,0.6)] hover:shadow-[0_0_40px_rgba(255,42,141,0.9)] transition-all duration-300 hover:scale-105 active:scale-95"
            >
              <i className="fa-solid fa-ticket text-xl" aria-hidden="true"></i>
              <span>Køb billet på Eventbilletten</span>
            </a>
            <Link
              to="/eventet"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-slate-900/80 hover:bg-slate-800 border border-slate-700 hover:border-pink-500/50 text-white font-bold text-sm uppercase tracking-wider transition-all duration-300"
            >
              <span>Læs om eventet</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>
          </div>

          {/* COUNTDOWN */}
          <div className="mt-14 w-full">
            <Countdown 
              targetDate={eventData?.dato || "2026-11-14"} 
              eventTitle={eventData?.eventnavn || "ØrBike 2026"} 
            />
          </div>
        </div>
      </section>

      {/* HURTIGT EVENT-OVERBLIK (KRAFTFULDE METRICS & FAKTA) */}
      <section className="py-12 bg-[#0c0e17] border-y border-pink-500/20">
        <div className="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 text-center">
            {/* Dato */}
            <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/70 border border-pink-500/20 hover:border-pink-500/40 transition-colors">
              <div className="w-10 h-10 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center mx-auto mb-2 text-lg">
                <i className="fa-solid fa-calendar" aria-hidden="true"></i>
              </div>
              <div className="text-xl sm:text-2xl font-black font-display text-white uppercase">14. NOV 2026</div>
              <div className="text-xs text-slate-400 font-medium mt-1">Lørdag kl. 07.30 - 20.30</div>
            </div>

            {/* Lokation */}
            <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/70 border border-pink-500/20 hover:border-pink-500/40 transition-colors">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center mx-auto mb-2 text-lg">
                <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
              </div>
              <div className="text-xl sm:text-2xl font-black font-display text-white uppercase">Ørbæk Midtpunkt</div>
              <div className="text-xs text-slate-400 font-medium mt-1">Midt i Danmark (Fyn)</div>
            </div>

            {/* Kapacitet */}
            <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/70 border border-pink-500/20 hover:border-pink-500/40 transition-colors">
              <div className="w-10 h-10 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center mx-auto mb-2 text-lg">
                <i className="fa-solid fa-bicycle" aria-hidden="true"></i>
              </div>
              <div className="text-xl sm:text-2xl font-black font-display text-white uppercase">+230 Cykler</div>
              <div className="text-xs text-slate-400 font-medium mt-1">1.000+ deltagere</div>
            </div>

            {/* Timer */}
            <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/70 border border-pink-500/20 hover:border-pink-500/40 transition-colors">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center mx-auto mb-2 text-lg">
                <i className="fa-solid fa-stopwatch" aria-hidden="true"></i>
              </div>
              <div className="text-xl sm:text-2xl font-black font-display text-white uppercase">10 Timer</div>
              <div className="text-xs text-slate-400 font-medium mt-1">Non-stop puls & musik</div>
            </div>

            {/* Formål */}
            <div className="col-span-1 p-4 sm:p-5 rounded-2xl bg-gradient-to-b from-pink-950/40 to-slate-900/70 border border-pink-500/40 shadow-[0_0_15px_rgba(255,42,141,0.2)]">
              <div className="w-10 h-10 rounded-full bg-pink-500/30 text-pink-300 flex items-center justify-center mx-auto mb-2 text-lg">
                <i className="fa-solid fa-heart" aria-hidden="true"></i>
              </div>
              <div className="text-xl sm:text-2xl font-black font-display text-pink-300 uppercase">Godt formål</div>
              <div className="text-xs text-slate-300 font-medium mt-1">Børn m/ kritisk sygdom</div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRODUKTION TIL ØRBIKE */}
      <section className="py-20 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-purple-500/20 text-purple-400 border border-purple-500/30">
              En bike fest uden lige
            </span>
            <h2 className="text-3xl sm:text-5xl font-black text-white font-display uppercase tracking-tight leading-tight">
              Spinning, koncert, fest og velgørenhed i én eksplosion af energi
            </h2>
            <p className="text-slate-300 text-base leading-relaxed">
              ØrBike er med +230 cykler et af Danmarks største indoor bike events og placeret lige midt i Danmark i Ørbæk Midtpunkt!
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              ØrBike eventet har til formål at skabe en indoor bike fest til fordel for en god sag. Ved deltagelse i ØrBike støtter du et godt formål og giver dermed din træning endnu mere mening. Siden 2014 har vi samlet sved, smil og rytmere fra hele landet.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/eventet"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-pink-600 hover:bg-pink-500 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-[0_0_20px_rgba(255,42,141,0.3)]"
              >
                <span>Læs hele eventprogrammet</span>
                <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
              </Link>
              <a
                href={billetlink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 border border-pink-500/30 text-pink-400 hover:text-white font-bold text-xs uppercase tracking-wider transition-colors"
              >
                <i className="fa-solid fa-ticket" aria-hidden="true"></i>
                <span>Køb billet</span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <img
                src="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/dreamfour-9883.jpg"
                alt="ØrBike Salen"
                className="rounded-2xl border border-pink-500/20 object-cover w-full h-56 shadow-lg"
              />
              <img
                src="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/dreamfour-7447.jpg"
                alt="ØrBike Stemning"
                className="rounded-2xl border border-pink-500/20 object-cover w-full h-64 shadow-lg"
              />
            </div>
            <div className="space-y-4 pt-8">
              <img
                src="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/dreamfour-9824.jpg"
                alt="ØrBike Instruktører"
                className="rounded-2xl border border-pink-500/20 object-cover w-full h-64 shadow-lg"
              />
              <img
                src="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/dreamfour-7410.jpg"
                alt="ØrBike Smil"
                className="rounded-2xl border border-pink-500/20 object-cover w-full h-56 shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* GODT FORMÅL HIGHLIGHT SECTION (STORE TAL FRA CMS) */}
      <section className="py-20 bg-gradient-to-b from-[#0e101a] to-[#090a0f] border-t border-pink-500/20 relative">
        <div className="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-pink-950/40 via-purple-950/30 to-slate-900/60 border border-pink-500/30 rounded-3xl p-8 sm:p-12 shadow-[0_0_40px_rgba(255,42,141,0.15)] relative overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 space-y-4">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/40">
                  Hjertet på det rette sted
                </span>
                <h3 className="text-3xl sm:text-5xl font-black text-white font-display uppercase tracking-tight">
                  For børn med kritisk sygdom
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Via Team Rynkeby donerer ØrBike minimum 70% af overskuddet fra 2025 og frem til børn med kritisk sygdom. Når du deltager i ØrBike, er du derfor med til at skabe en uforglemmelig dag på cyklen – og samtidig gøre en forskel for børn og familier, der kæmper med alvorlig sygdom.
                </p>
                <div className="pt-3">
                  <Link
                    to="/godt-formaal"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-xs uppercase tracking-wider shadow-lg hover:scale-105 transition-transform"
                  >
                    <span>Læs om modtagere & historik</span>
                    <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>

              {/* Stort Indsamlet Beløb fra CMS */}
              <div className="lg:col-span-5 flex flex-col items-center justify-center p-6 sm:p-8 bg-slate-900/90 rounded-2xl border border-pink-500/30 text-center">
                <div className="text-xs uppercase tracking-widest text-slate-400 font-bold font-mono mb-2">
                  Donation i 2025
                </div>
                <div className="text-5xl sm:text-6xl font-black text-white font-display uppercase tracking-tight text-neon-pink">
                  25.000 kr.
                </div>
                <div className="text-xs text-slate-300 font-semibold mt-1">
                  doneret til Børnecancerfonden & Børnelungefonden
                </div>
                <div className="mt-4 pt-4 border-t border-slate-800 w-full flex items-center justify-around text-xs text-slate-400">
                  <div>
                    <span className="block font-bold text-white font-display text-base">30.000 kr.</span>
                    <span>i 2024</span>
                  </div>
                  <div className="h-6 w-px bg-slate-800"></div>
                  <div>
                    <span className="block font-bold text-white font-display text-base">15.000 kr.</span>
                    <span>i 2023</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BILLETTER & KATEGORIER */}
      <section className="py-20 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-purple-500/20 text-purple-400 border border-purple-500/30 mb-3">
            Sikre din plads på cyklen
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-white font-display uppercase tracking-tight">
            Billetter til ØrBike 2026
          </h2>
          <p className="text-slate-300 text-sm sm:text-base mt-2">
            Billetter købes eksternt via Eventbilletten. Vælg mellem Priority cykler til firmaer/teams eller Essential cykler, samt forplejning for hele dagen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Priority Cykel */}
          <div className="bg-gradient-to-b from-[#181a29] to-[#0f111d] border-2 border-pink-500/50 rounded-3xl p-8 shadow-[0_0_30px_rgba(255,42,141,0.25)] relative flex flex-col justify-between">
            <div className="absolute top-4 right-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-[10px] font-extrabold uppercase px-3 py-1 rounded-full tracking-wider">
              Populær for firmaer
            </div>
            <div>
              <span className="text-xs uppercase font-mono font-bold text-pink-400">Priskategori 1</span>
              <h3 className="text-3xl font-black font-display text-white uppercase mt-1">Priority Cykel</h3>
              <p className="text-slate-400 text-xs mt-1">Perfekt til virksomheder, medarbejderteams eller vennegrupper</p>

              <div className="mt-6 mb-6">
                <span className="text-4xl sm:text-5xl font-black font-display text-white">5.550 kr.</span>
                <span className="text-xs text-slate-400 block mt-1">inkl. moms pr. cykel - ekskl. ordregebyr</span>
              </div>

              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-pink-500 mt-1" aria-hidden="true"></i>
                  <span>10 timers spinning non-stop</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-pink-500 mt-1" aria-hidden="true"></i>
                  <span>Placering på de 3 forreste rækker med optimal udsigt</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-pink-500 mt-1" aria-hidden="true"></i>
                  <span>Op til 10 deltagere pr. cykel (f.eks. 1 ny pr. time)</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-pink-500 mt-1" aria-hidden="true"></i>
                  <span>Profilering af logo / video i pauser og på storskærme</span>
                </li>
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-800">
              <a
                href={billetlink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wider shadow-lg hover:scale-[1.02] transition-transform"
              >
                <span>Køb Priority Cykel</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
              </a>
            </div>
          </div>

          {/* Essential Cykel */}
          <div className="bg-[#12141f] border border-slate-800 hover:border-pink-500/40 rounded-3xl p-8 relative flex flex-col justify-between transition-colors">
            <div>
              <span className="text-xs uppercase font-mono font-bold text-purple-400">Priskategori 2</span>
              <h3 className="text-3xl font-black font-display text-white uppercase mt-1">Essential Cykel</h3>
              <p className="text-slate-400 text-xs mt-1">Til cykelentusiaster og mindre teams</p>

              <div className="mt-6 mb-6">
                <span className="text-4xl sm:text-5xl font-black font-display text-white">1.275 kr.</span>
                <span className="text-xs text-slate-400 block mt-1">inkl. moms pr. cykel - ekskl. ordregebyr</span>
              </div>

              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-purple-400 mt-1" aria-hidden="true"></i>
                  <span>10 timers spinning</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-purple-400 mt-1" aria-hidden="true"></i>
                  <span>Placering centralt for scenen</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-purple-400 mt-1" aria-hidden="true"></i>
                  <span>Op til 5 personer pr. cykel (ekstra kan tilkøbes)</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-check text-purple-400 mt-1" aria-hidden="true"></i>
                  <span>Fuld adgang til alle 10 timer og fællesområder</span>
                </li>
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-800">
              <a
                href={billetlink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-full bg-slate-800 hover:bg-slate-700 text-white font-bold text-sm uppercase tracking-wider transition-colors"
              >
                <span>Køb Essential Cykel</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/billetter"
            className="text-sm font-semibold text-pink-400 hover:text-pink-300 underline decoration-pink-500/40"
          >
            Se alle informationer om forplejningsbilletter (morgenmad, frokost, aftensmad) →
          </Link>
        </div>
      </section>

      {/* STEMNINGSSEKTION MED VIDEO OG BILLEDER */}
      <StemningSection videoer={videoer} gallerier={gallerier} />

      {/* PROGRAM TIMELINE HIGHLIGHT */}
      <section className="py-20 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30 mb-3">
            Dagens program
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-white font-display uppercase tracking-tight">
            10 timer – én kæmpe fest
          </h2>
          <p className="text-slate-300 text-sm mt-2">
            Instruktørerne kører cirka 50–53 minutter med god energi og motiverende musik.
          </p>
        </div>

        <div className="space-y-3">
          {program.map((item, idx) => (
            <div
              key={item.id || idx}
              className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-5 rounded-xl bg-[#12141e] border border-slate-800 hover:border-pink-500/40 transition-colors gap-3"
            >
              <div className="flex items-center gap-4">
                <span className="px-3 py-1.5 rounded-lg bg-pink-500/20 text-pink-400 font-display font-bold text-lg min-w-[5.5rem] text-center">
                  {item.tidspunkt}
                </span>
                <div>
                  <h4 className="font-bold text-white text-base sm:text-lg font-display uppercase">
                    {item.titel}
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-400">
                    {item.beskrivelse}
                  </p>
                </div>
              </div>

              {item.instruktor_artist && (
                <div className="sm:text-right shrink-0 text-xs font-medium text-purple-300 flex items-center gap-1.5">
                  <i className="fa-solid fa-microphone text-pink-400" aria-hidden="true"></i>
                  <span>{item.instruktor_artist}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/eventet"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-slate-900 border border-pink-500/40 text-pink-400 hover:text-white hover:bg-pink-600 hover:border-pink-600 font-bold text-xs uppercase tracking-wider transition-colors"
          >
            <span>Læs mere om instruktører & forplejning</span>
            <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
          </Link>
        </div>
      </section>

      {/* CITATER / UDTALELSER FRA INSTRUKTØRER (Bobby Croshaw & Johnny Roland) */}
      <section className="py-20 bg-[#0c0e17] border-y border-pink-500/20">
        <div className="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-widest font-bold text-purple-400 font-mono">
              Stemmer fra scenen
            </span>
            <h3 className="text-3xl sm:text-4xl font-black text-white font-display uppercase mt-1">
              Hvad siger instruktørerne om ØrBike?
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Bobby Croshaw */}
            <div className="bg-slate-900/90 border border-pink-500/25 rounded-2xl p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-pink-500/20 border border-pink-500/40 flex items-center justify-center text-pink-400 text-xl font-bold">
                    <i className="fa-solid fa-quote-left" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg font-display uppercase">Bobby Croshaw</h4>
                    <p className="text-xs text-pink-400 font-medium">Indoor Bike Ikon • Sidste Bike-time til ØrBike 2026</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed italic">
                  "For mig har Indoor Bike altid handlet om nærvær. Om at mærke deltagernes energi, smil og råb og omsætte det til en unik oplevelse på cyklen. Efter mange år har jeg besluttet, at min sidste store optræden bliver den 14. november til ØrBike. Det skal nok blive en fest!"
                </p>
              </div>
            </div>

            {/* Johnny Roland */}
            <div className="bg-slate-900/90 border border-purple-500/25 rounded-2xl p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 border border-purple-500/40 flex items-center justify-center text-purple-400 text-xl font-bold">
                    <i className="fa-solid fa-quote-left" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg font-display uppercase">Johnny Roland aka El'Diablo</h4>
                    <p className="text-xs text-purple-400 font-medium">25 års jubilæum som instruktør</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed italic">
                  "Tidligere har jeg deltaget på næsten samtlige store events herhjemme, og er dybt taknemmelig for at, jeg igen er blevet inviteret til, at køre på ØrBike, hvilket jeg glæder mig enormt meget til. HUSK: Ingen kan alt, men alle kan noget og sammen kan vi det hele!"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ØrBike 365 Medlemskab */}
      <section className="py-12 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
        <OrBike365Section />
      </section>

      {/* PARTNERE LOGO-GRID */}
      <section className="py-20 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
            Tak til vores støtter
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-white font-display uppercase tracking-tight mt-1">
            Partnere & Sponsorer
          </h2>
          <p className="text-slate-400 text-sm mt-2">
            Støt dem, der får ØrBike til at køre! Med deres støtte – økonomisk, praktisk og med udstyr – skabes rammerne for en uforglemmelig dag.
          </p>
        </div>

        {/* PARTNERE AUTOPLAY SLIDER */}
        <div className="relative w-full overflow-hidden py-4">
          {/* Gradient fades i siderne */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 sm:w-28 bg-gradient-to-r from-[#090a0f] to-transparent z-10"></div>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 sm:w-28 bg-gradient-to-l from-[#090a0f] to-transparent z-10"></div>

          <div className="animate-partner-marquee flex items-center">
            {(partnere.length > 0 ? [...partnere, ...partnere, ...partnere, ...partnere] : [])
              .filter(p => {
                const url = p.logo?.[0] || '';
                const name = (p.partnernavn || '').toLowerCase();
                // Exclude Foliekniven and Børnecancerfonden from the marquee per request
                if (url.includes('b95b4ebb') || name.includes('børnecancerfonden')) return false;
                if (url.includes('70ead052') || name.includes('foliekniven')) return false;
                return true;
              })
              .map((p, idx) => {
                const logoUrl = p.logo?.[0] || '';

              return (
                <div
                  key={`${p.id || p.partnernavn || idx}-${idx}`}
                  className="shrink-0 mx-8 sm:mx-12 md:mx-14 flex items-center justify-center transition-transform duration-300 hover:scale-105 group"
                >
                  {p.website ? (
                    <a
                      href={p.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center p-2 focus:outline-none"
                      title={p.partnernavn}
                      aria-label={p.partnernavn}
                    >
                      <WhiteLogo
                        src={logoUrl}
                        alt={p.partnernavn}
                        className="h-16 sm:h-20 md:h-24 w-auto max-w-[240px] sm:max-w-[280px] max-h-20 sm:max-h-24 object-contain opacity-95 group-hover:opacity-100 transition-all duration-300 drop-shadow-[0_2px_14px_rgba(255,255,255,0.2)]"
                      />
                    </a>
                  ) : (
                    <div className="flex items-center justify-center p-2" title={p.partnernavn}>
                      <WhiteLogo
                        src={logoUrl}
                        alt={p.partnernavn}
                        className="h-16 sm:h-20 md:h-24 w-auto max-w-[240px] sm:max-w-[280px] max-h-20 sm:max-h-24 object-contain opacity-95 group-hover:opacity-100 transition-all duration-300 drop-shadow-[0_2px_14px_rgba(255,255,255,0.2)]"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/partnere"
            className="text-xs uppercase font-bold text-pink-400 hover:text-pink-300 underline tracking-wider"
          >
            Se alle samarbejdspartnere og læs om partnerskabsmuligheder →
          </Link>
        </div>
      </section>
    </div>
  );
}
